import { PrinterIcon } from "lucide-react";
import { useCallback } from "react";

import { CommandMenu, useCommandMenu } from "@/components/CommandMenu";
import {
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from "@/components/ui/command";
import { Text } from "@/components/ui/text";
import { useTinyKeys } from "@/lib/useTinyKeys";
import type { TestRunDetails, WorklistNew } from "../types";
import { worklistCodes } from "../utils";
import { usePrintWorklistsPdf } from "./PrintWorklistsButton";

interface TestRunActionsProps {
  run: TestRunDetails;
  worklists: Pick<WorklistNew, "id" | "name" | "originalTestSpecs">[];
}

export function TestRunActions({ run, worklists }: TestRunActionsProps) {
  const { page, setPages, setOpen } = useCommandMenu();
  const { print } = usePrintWorklistsPdf({
    run,
    worklists,
    onPrintDialogClose: () => {
      setOpen(false);
    },
  });

  const printAction = useCallback(() => {
    setPages([{ id: "print-worklists", label: "Print worklists" }]);
    setOpen(true);
  }, [setPages, setOpen]);

  useTinyKeys(
    {
      p: (evt) => {
        evt.preventDefault();
        printAction();
      },
    },
    [printAction],
  );

  return (
    <CommandMenu>
      {!page && (
        <CommandGroup heading="Test run">
          <CommandItem onSelect={() => printAction()}>
            <PrinterIcon />
            <span>Print worklists</span>
            <CommandShortcut>P</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      )}
      {page?.id === "print-worklists" && (
        <CommandGroup heading="Print worklists">
          <CommandItem onSelect={() => print()}>
            <PrinterIcon />
            <span>Print all</span>
          </CommandItem>
          {worklists.map((worklist) => (
            <CommandItem
              key={worklist.id}
              onSelect={() => print(worklist.id)}
              className="w-full min-w-0 gap-2"
            >
              <PrinterIcon />
              <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
                <Text size="2" className="min-w-0 truncate">
                  {worklist.name}
                </Text>
                <Text
                  size="1"
                  muted
                  className="shrink-0 font-mono tabular-nums"
                >
                  {worklistCodes(worklist)}
                </Text>
              </span>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </CommandMenu>
  );
}
